/**
 * LinkStyleDialog — attach an existing Style File to ONE garment on an order
 * (ADR-0043). The order itself reaches `linked` only once every garment has a
 * Style File. Shared by the Orders list and the order detail page.
 */
import { useEffect, useState } from "react";
import { Link } from "@tanstack/react-router";
import {
  Button,
  DialogRoot,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
} from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";

export interface LinkStyleDialogOrder {
  /** The GARMENT (order item) being linked, not the order itself. */
  itemId: string;
  orderNo: string;
  /** The garment's own name. */
  name: string;
}

export function LinkStyleDialog({
  order,
  onClose,
}: {
  order: LinkStyleDialogOrder | null;
  onClose: () => void;
}) {
  const utils = trpc.useUtils();
  const [styleId, setStyleId] = useState("");
  useEffect(() => {
    setStyleId("");
  }, [order?.itemId]);

  const styles = trpc.styles.list.useQuery(
    { limit: 100, excludeLinked: true },
    { enabled: order !== null },
  );
  const link = trpc.garmentOrders.linkStyle.useMutation({
    onSuccess: async (result) => {
      await Promise.all([
        utils.garmentOrders.list.invalidate(),
        utils.garmentOrders.get.invalidate(),
      ]);
      toast.success("Style file linked", {
        description:
          result.status === "linked"
            ? `${order?.orderNo} is now fully in engineering.`
            : `${order?.orderNo}: this garment is now in engineering.`,
      });
      onClose();
    },
    onError: (err) => {
      toast.error("Couldn't link the style file", { description: err.message });
    },
  });

  return (
    <DialogRoot open={order !== null} onOpenChange={(open) => (!open ? onClose() : undefined)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Link a Style File</DialogTitle>
          <DialogDescription>
            {order
              ? `${order.orderNo} · ${order.name} — pick the Style File engineering opened for this garment.`
              : ""}
          </DialogDescription>
        </DialogHeader>
        <Select value={styleId} onValueChange={setStyleId} disabled={styles.isLoading}>
          <SelectTrigger className="h-9 text-sm">
            <SelectValue
              placeholder={styles.isLoading ? "Loading style files…" : "Select style file"}
            />
          </SelectTrigger>
          <SelectContent>
            {(styles.data?.items ?? []).map((s) => (
              <SelectItem key={s.id} value={s.id}>
                {s.styleRef} · {s.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {styles.data && styles.data.items.length === 0 ? (
          <p className="text-sm text-muted">
            No unlinked Style Files —{" "}
            <Link to="/merchandising/styles/new" className="text-sky-700 hover:underline">
              open one first
            </Link>
            , then link it here.
          </p>
        ) : null}
        <DialogFooter>
          <Button intent="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button
            disabled={!order || styleId === "" || link.isPending}
            onClick={() => {
              if (order && styleId) link.mutate({ itemId: order.itemId, styleId });
            }}
          >
            {link.isPending ? "Linking…" : "Link style file"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogRoot>
  );
}
